/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { FormattedHTMLMessage, FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Grid, Typography, Typography2, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { DegreesHubDegreeMarketingInformation } from 'bundles/front-page/__generated__/baseContentfulTypes';
import type { CareerRolesFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import ProductCards from 'bundles/front-page/components/modules/career-roles/ProductCards';
import SalaryBar from 'bundles/front-page/components/modules/career-roles/SalaryBar';
import Testimonial from 'bundles/front-page/components/modules/career-roles/Testimonial';

import _t from 'i18n!nls/front-page';

type Props = {
  role?: CareerRolesFieldsFragment | null;
  countryCode?: string;
};

export const baseSalaryBarWidth = 50;

const styles = {
  panel: (theme: Theme) => css`
    margin: ${theme.spacing(24, 0)};

    ${theme.breakpoints.up('sm')} {
      padding: ${theme.spacing(48, 32)};
      background: var(--cds-color-white-0);
      border-radius: 8px;
      border: 2px solid ${theme.palette.gray[300]};
      margin: ${theme.spacing(16, 0)};
    }

    ${theme.breakpoints.up('lg')} {
      padding: ${theme.spacing(48, 80)};
    }
  `,
  // Overrides h1 font-size temporarily.
  // TODO: Should be removed after upgrading to the next CDS version.
  h1SemiBoldOverride: (theme: Theme) => css`
    font-size: 28px;

    ${theme.breakpoints.up('sm')} {
      font-size: 32px;
    }
  `,
  headerDescription: (theme: Theme) => css`
    margin: ${theme.spacing(8, 0, 0)};

    ${theme.breakpoints.up('sm')} {
      margin: ${theme.spacing(8, 0, 8)};
    }
  `,
  section: (theme: Theme) => css`
    padding: ${theme.spacing(24, 0)};

    & + & {
      border-top: 1px solid ${theme.palette.gray[300]};
    }
  `,
  sectionHeader: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(8)};

    ${theme.breakpoints.up('sm')} {
      margin-bottom: ${theme.spacing(16)};
    }
  `,
  jobPostingText: (theme: Theme) => css`
    font-weight: 600;

    strong {
      font-weight: inherit;
      color: ${theme.palette.black[500]};
    }

    & + & {
      margin-top: ${theme.spacing(16)};
    }
  `,
  testimonal: (isDesktop: boolean, theme: Theme) => css`
    display: ${isDesktop ? 'none' : 'block'};

    ${theme.breakpoints.up('lg')} {
      display: ${isDesktop ? 'block' : 'none'};
    }
  `,
  disclaimer: (theme: Theme) => css`
    margin-top: ${theme.spacing(8)};
    padding-top: ${theme.spacing(32)};
    border-top: 1px solid ${theme.palette.gray[300]};
    white-space: pre-wrap;
  `,
  productCardsSection: (theme: Theme) => css`
    ${theme.breakpoints.up('lg')} {
      padding-left: ${theme.spacing(32)};
    }
  `,
};

export const getSalaryBarWidth = (
  mediumSalaryWorkingProfessional?: number | null,
  mediumSalaryCareerRole?: number | null
) => {
  if (!mediumSalaryCareerRole || !mediumSalaryWorkingProfessional) {
    return 0;
  }

  const calculatedWidth = (mediumSalaryCareerRole * baseSalaryBarWidth) / mediumSalaryWorkingProfessional;

  // Max width is 100% to prevent the bar from overflowing.
  return Math.min(100, calculatedWidth);
};

export const getProductSlugs = (role: CareerRolesFieldsFragment | null) =>
  role?.productProgramsCollection?.items
    ?.filter((product): product is DegreesHubDegreeMarketingInformation => !!product)
    .map((product) => product.slug)
    .filter((slug): slug is string => !!slug);

export const CareerRolePanel = ({ role, countryCode }: Props) => {
  const theme = useTheme();
  const salaryBarWidth = useMemo(() => {
    return getSalaryBarWidth(role?.mediumSalaryWorkingProfessional, role?.mediumSalaryCareerRole);
  }, [role?.mediumSalaryWorkingProfessional, role?.mediumSalaryCareerRole]);

  if (!role || !role?.title) {
    return null;
  }

  const showMedianSalary = role?.mediumSalaryCareerRole && role?.mediumSalaryWorkingProfessional;
  const productSlugs = getProductSlugs(role);
  const showProductCards = productSlugs?.length;

  return (
    <Grid container css={styles.panel}>
      <Grid item xs={12}>
        <Typography variant="h1semibold" component="h3" css={styles.h1SemiBoldOverride}>
          {role.title}
        </Typography>
        {role.description && (
          <Typography2 component="p" variant="bodyPrimary" css={styles.headerDescription} color="supportText">
            {role.description}
          </Typography2>
        )}
      </Grid>
      <Grid item lg={showProductCards ? 5 : 12} xs={12}>
        {showMedianSalary && (
          <div css={styles.section}>
            <Typography variant="h2semibold" component="h4" css={styles.sectionHeader}>
              {_t('Median Salary')}
            </Typography>
            <div>
              <SalaryBar
                roleTitle={_t('All occupations')}
                salary={role.mediumSalaryWorkingProfessional}
                footnoteReference="*"
                width={baseSalaryBarWidth}
                variant="base"
                countryCode={countryCode}
              />
              <SalaryBar
                roleTitle={role.title}
                salary={role.mediumSalaryCareerRole}
                footnoteReference="**"
                width={salaryBarWidth}
                countryCode={countryCode}
              />
            </div>
          </div>
        )}
        <div css={styles.section}>
          {role?.jobPostings && (
            <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.jobPostingText}>
              <FormattedHTMLMessage
                message={_t('Job openings: <strong>{numberOfPostings}<sup>**</sup></strong>')}
                numberOfPostings={<FormattedNumber value={role.jobPostings} />}
              />
            </Typography2>
          )}
          {role?.projected10YearGrowth && (
            <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.jobPostingText}>
              <FormattedHTMLMessage
                message={_t('Projected 10 year growth: <strong>{growth}%<sup>***</sup></strong>')}
                growth={
                  role.projected10YearGrowth > 0 ? (
                    <span>
                      +<FormattedNumber value={role.projected10YearGrowth} />
                    </span>
                  ) : (
                    <FormattedNumber value={role.projected10YearGrowth} />
                  )
                }
              />
            </Typography2>
          )}
        </div>
        {role?.testimonial && (
          <div css={[styles.section, styles.testimonal(true, theme)]}>
            <Testimonial
              text={role.testimonial.text}
              authorName={role.testimonial.authorName}
              imageUrl={role.testimonial.image?.url}
            />
          </div>
        )}
      </Grid>
      {showProductCards && (
        <Grid item lg={7} xs={12}>
          <div css={[styles.section, styles.productCardsSection]}>
            <Typography variant="h2semibold" component="h4" css={styles.sectionHeader}>
              <FormattedMessage
                message={_t(
                  'Recommended {numOfProducts, plural, =1 {Professional Certificate} other {Professional Certificates}}'
                )}
                numOfProducts={productSlugs.length}
              />
            </Typography>
            <ProductCards slugs={productSlugs} roleName={role.title} />
          </div>
        </Grid>
      )}
      {role?.testimonial && (
        <div css={[styles.section, styles.testimonal(false, theme)]}>
          <Testimonial
            text={role.testimonial.text}
            authorName={role.testimonial.authorName}
            imageUrl={role.testimonial.image?.url}
          />
        </div>
      )}
    </Grid>
  );
};

export default CareerRolePanel;
